import React from 'react';
import { Plug, Boxes, ArrowRight, CheckCircle2 } from 'lucide-react';

const PLATFORMS = [
  {
    name: 'Turvo',
    tag: 'TMS',
    blurb: 'Bi-directional sync for tenders, status updates, and accepted rates.',
    status: 'Live now',
  },
  {
    name: 'Rose Rocket',
    tag: 'TMS',
    blurb: 'Push loads into Vibe, pull awarded carriers back into your dispatch board.',
    status: 'Live now',
  },
  {
    name: 'McLeod',
    tag: 'TMS',
    blurb: 'Load tender, award, and rate data synced with PowerBroker and LoadMaster.',
    status: 'Live now',
  },
  {
    name: 'NetSuite',
    tag: 'ERP',
    blurb: 'Auto-create freight POs and reconcile awarded rates with finance.',
    status: 'Live now',
  },
  {
    name: 'SAP',
    tag: 'ERP',
    blurb: 'Connect transportation management and freight accruals to your SAP landscape.',
    status: 'Available',
  },
  {
    name: 'Custom WMS',
    tag: 'WMS',
    blurb: 'Warehouse execution and shipment triggers — scoped and built by our integrations team.',
    status: 'Custom',
  },
];

export default function Integrations() {
  return (
    <section id="integrations" className="bg-ink-50 border-y border-ink-200">
      <div className="container-page py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">Integrations</p>
            <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
              Plugs into the systems you already run on.
            </h2>
            <p className="mt-5 text-lg text-ink-600 text-balance">
              Vibe Engine doesn’t ask you to rip out your TMS, ERP, or WMS. We
              ship native connectors for the platforms shippers use most — and our
              team builds custom integrations when your stack needs something
              bespoke.
            </p>

            <ul className="mt-7 space-y-3 text-sm text-ink-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                TMS &amp; ERP integrations with Turvo, Rose Rocket, McLeod,
                NetSuite, SAP, and more
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                Custom ERP, TMS, and WMS connectors scoped and maintained by our
                integrations team
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                REST API &amp; webhooks for in-house tooling
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                White-glove onboarding by our integrations team
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {PLATFORMS.map((n) => (
                <div key={n.name} className="card-hover p-5">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-3">
                      <IntegrationMark name={n.name} />
                      <div className="text-base font-semibold text-ink-900">
                        {n.name}
                      </div>
                    </div>
                    <span className="pill">{n.tag}</span>
                  </div>
                  <p className="mt-3 text-sm text-ink-600 leading-relaxed">
                    {n.blurb}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-ink-700">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        n.status === 'Live now' ? 'bg-emerald-500' : 'bg-ink-400'
                      }`}
                    />
                    {n.status}
                  </div>
                </div>
              ))}

              <div className="card-dark p-5 col-span-1 sm:col-span-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold text-white">
                      TMS &amp; ERP integrations
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      Custom connectors for your ERP, TMS, and WMS — plus named
                      platforms out of the box.
                    </div>
                  </div>
                  <a
                    href="#demo"
                    className="inline-flex items-center gap-1 text-sm font-medium text-white hover:text-white/90 shrink-0"
                  >
                    Talk to us
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
                <p className="mt-4 text-sm text-white/85 leading-relaxed">
                  Whether you run SAP, NetSuite, Turvo, Rose Rocket, McLeod, or
                  an in-house stack, we scope, build, and maintain the
                  integration. Documented REST API and webhooks cover every step
                  of the tender lifecycle — most custom connectors go live in
                  under four weeks.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {[
                    'SAP',
                    'NetSuite',
                    'Turvo',
                    'Rose Rocket',
                    'McLeod',
                    'REST API',
                    'Webhooks',
                  ].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center text-xs font-medium rounded-full bg-white/10 text-white px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3 rounded-xl border border-ink-200 bg-white px-4 py-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-ink-100 text-ink-900 shrink-0">
                <Boxes className="h-4 w-4" />
              </span>
              <div className="text-sm text-ink-700">
                <span className="font-semibold text-ink-900">Don’t see your system?</span>{' '}
                We’ll build the connector for your TMS, ERP, or WMS. Tell us what
                you run on and we’ll come back with a timeline.
              </div>
              <a href="#demo" className="ml-auto btn-ghost text-sm shrink-0">
                <Plug className="h-4 w-4" />
                Request
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IntegrationMark({ name }) {
  const initials = name
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-ink-900 text-white text-xs font-semibold tracking-wider">
      {initials}
    </span>
  );
}
